import { Box, Circle, Text, colors } from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import { useFinancialWorkspace } from 'modules/finance/components/FinancialWorkspace/Providers/Provider'
import { useTotal } from 'modules/finance/components/FinancialWorkspace/Providers/TotalProvider'
import EmptyState from 'modules/finance/components/FinancialWorkspace/components/EmptyState'
import { ChartData } from 'modules/finance/components/FinancialWorkspace/components/Table/types'
import { centToFormattedAmount } from 'modules/finance/components/FinancialWorkspace/components/chart/helpers'
import { LineChart } from 'modules/finance/components/FinancialWorkspace/components/chart/index'

const PayrollChart = () => {
  const { formattedState: variables } = useFinancialWorkspace()
  const { plotData, loading, error } = useTotal()
  const {
    access: { locale: accessLocale },
    company: { currency },
  } = useCurrentSession()

  const total = useUnsafeMemo(() => {
    return (plotData || []).reduce(
      (accumulator, currentValue) => accumulator + (currentValue.y || 0),
      0
    )
  }, [plotData])

  const chartData: ChartData[] = useUnsafeMemo(() => {
    const data = []

    if (plotData) {
      data.push({
        data: [...plotData],
        serieId: 'laborCost',
        pointColor: colors.viridian1000,
        hoveredPointColor: colors.grey1000,
      })
    }

    return data
  }, [plotData])

  const supportLink = () => (
    <a
      target="_blank"
      rel="noopener noreferrer"
      href="https://help.factorialhr.com/en_US/managing-my-company-account/how-to-contact-customer-support"
    >
      {i18n.t('finance.financial_workspace.error.contact_support')}
    </a>
  )

  const toAmount = (value: number) => {
    const cents = Number(value) || 0
    return centToFormattedAmount(cents, accessLocale, currency)
  }

  const totals = loading ? null : (
    <>
      <Box flexDirection="row" gap="s8" alignItems="center">
        <>
          <Circle type="viridian" size={10} />
        </>
        <Text.Normal weight="medium" size="200">
          {i18n.t('finance.financial_workspace.payroll.total_company_costs')}
        </Text.Normal>
      </Box>
      <Box paddingLeft="s16">
        <Text.Normal color="grey1000" weight="bold" size="300">
          {toAmount(total).amount}
        </Text.Normal>
      </Box>
    </>
  )

  if (error) {
    return (
      <Box height="s300" width="full">
        <EmptyState
          icon="monitor-flash"
          title={i18n.t('finance.financial_workspace.error.generic_title')}
          description={i18n.tView('finance.financial_workspace.error.generic_description', {
            link: supportLink(),
          })}
        />
      </Box>
    )
  }

  return (
    <Box
      width="full"
      height="s300"
      padding="s16"
      borderRadius={{ all: 'abs016' }}
      border={{ all: { color: 'grey400', width: 's1', style: 'solid' } }}
    >
      {totals}
      <LineChart
        loading={loading}
        toAmount={toAmount}
        chartData={chartData}
        period={variables.period}
      />
    </Box>
  )
}

export { PayrollChart }
